export type BotTimoRole =
  | "bottimo-superadmin"
  | "bottimo-device-admin";

type Profile = Record<string, unknown>;

const managedRoles = new Set<BotTimoRole>(["bottimo-superadmin", "bottimo-device-admin"]);

function roleValues(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((role): role is string => typeof role === "string");
  }
  if (typeof value !== "string") return [];
  let text = value;
  try {
    const decoded = JSON.parse(text) as unknown;
    if (Array.isArray(decoded)) {
      return decoded.filter((role): role is string => typeof role === "string");
    }
    if (typeof decoded === "string") text = decoded;
  } catch {
    // Keycloak's single-valued realm-role mapper emits a bracketed string.
  }
  return text.split(/[\s,\[\]{}()"']+/).filter(Boolean);
}

export function rolesFromProfile(profile: Profile): Set<BotTimoRole> {
  const access = profile.realm_access;
  const realmRoles = access && typeof access === "object" && "roles" in access
    ? roleValues((access as { roles?: unknown }).roles)
    : [];
  const values = [...realmRoles, ...roleValues(profile.bottimo_roles)];
  return new Set(
    values.filter((role): role is BotTimoRole =>
      typeof role === "string" && managedRoles.has(role as BotTimoRole)),
  );
}

export function canEnroll(roles: Set<BotTimoRole>): boolean {
  return roles.has("bottimo-superadmin") || roles.has("bottimo-device-admin");
}

export function canReadAudit(roles: Set<BotTimoRole>): boolean {
  return roles.has("bottimo-superadmin") || roles.has("bottimo-device-admin");
}

export function canRevoke(roles: Set<BotTimoRole>): boolean {
  return roles.has("bottimo-superadmin") || roles.has("bottimo-device-admin");
}

export function canRevokeEmail(roles: Set<BotTimoRole>): boolean {
  return canRevoke(roles);
}

export function canExport(roles: Set<BotTimoRole>): boolean {
  return roles.has("bottimo-superadmin") || roles.has("bottimo-device-admin");
}

export function isSuperadmin(roles: Set<BotTimoRole>): boolean {
  return roles.has("bottimo-superadmin");
}
